// Check Wind's numbers and shapes, kept free of React and the maps so they can be tested: the wind cone,
// the distance rings, and the labels for the readout and the time scale.
import moment from 'moment'
import { compass16, normalizeDegrees } from '../../lib/weatherCenter'

const M_PER_DEG_LAT = 111320
const M_PER_YD = 0.9144
const RAD = Math.PI / 180

const DIR_LONG = {
  N: 'North', NNE: 'North-northeast', NE: 'Northeast', ENE: 'East-northeast',
  E: 'East', ESE: 'East-southeast', SE: 'Southeast', SSE: 'South-southeast',
  S: 'South', SSW: 'South-southwest', SW: 'Southwest', WSW: 'West-southwest',
  W: 'West', WNW: 'West-northwest', NW: 'Northwest', NNW: 'North-northwest'
}

export const dirLong = deg => DIR_LONG[compass16(deg)] || null

// Scent travels downwind: the wind blows FROM `deg`.
export const downwind = deg => normalizeDegrees(deg + 180)

// The point `east` and `north` metres from `origin`.
export function offset(origin, east, north) {
  return {
    lat: origin.lat + north / M_PER_DEG_LAT,
    lng: origin.lng + east / (M_PER_DEG_LAT * Math.cos(origin.lat * RAD))
  }
}

// The bearing from `a` to `b`, degrees clockwise from north.
export function bearingTo(a, b) {
  const east = (b.lng - a.lng) * M_PER_DEG_LAT * Math.cos(a.lat * RAD), north = (b.lat - a.lat) * M_PER_DEG_LAT
  return normalizeDegrees(Math.atan2(east, north) / RAD)
}

// Metres between two points, flat-earth: fine over the few miles Check Wind works in.
export function distanceM(a, b) {
  const east = (b.lng - a.lng) * M_PER_DEG_LAT * Math.cos(a.lat * RAD), north = (b.lat - a.lat) * M_PER_DEG_LAT
  return Math.hypot(east, north)
}

// ---- The wind cone ----------------------------------------------------------------------
// How far and how wide scent carries for a wind speed (mph), its gusts, the hour of day and the cloud cover
// (0-1, missing = clear): long and narrow in a steady midday wind, short and wide at night or in light,
// gusty air. Three tiers, from where deer can smell it faintly out to where it is strong. Lengths in metres.
// Contract §3 "B revised" (Matt, 3 Nov): stability from the Pasquill class, length peaking in moderate wind.
export function plume(speed, gust, hour, cloud) {
  const spd = Math.max(0.5, speed || 0)
  const g = Math.max(0, Math.min(2, (gust || spd) / spd - 1))
  const cls = pasquill(spd * 0.44704, typeof cloud === 'number' && Number.isFinite(cloud) ? cloud : 0, hour)
  let a = atClass(CLASS_A, cls), rf = atClass(CLASS_RF, cls)
  a *= 1 + 0.35 * g
  rf /= 1 + 0.25 * g
  if (spd < 3) a += 0.15 * (3 - spd) / 3
  const r1 = Math.max(60, 700 * (1 - Math.exp(-0.2 * spd)) * Math.exp(-Math.max(0, spd - 9) * 0.035) * rf)
  return { a, R: [r1, r1 * 0.6, r1 * 0.3] }
}

// Briggs rural spread and the length factor for Pasquill classes A (0) to F (5).
const CLASS_A = [0.22, 0.16, 0.11, 0.08, 0.06, 0.04]
const CLASS_RF = [0.55, 0.65, 0.85, 1.0, 1.15, 1.35]
// A half class reads the mean of its two neighbours.
const atClass = (list, i) => (list[Math.floor(i)] + list[Math.ceil(i)]) / 2
// Daytime classes by sun (strong, moderate, slight) and wind column (u < 2, < 3, < 5, < 6, else).
const DAY_CLASS = [[0, 0.5, 1, 2, 2], [0.5, 1, 1.5, 2.5, 3], [4, 3.5, 3, 3, 3]]

// The Pasquill stability class for wind `u` m/s, cloud 0-1 and the local hour.
export function pasquill(u, cloud, hour) {
  if (cloud >= 0.875) return 3
  if (hour >= 20 || hour < 6) {
    if (u < 3) return cloud >= 0.5 ? 4 : 5
    if (u < 5) return cloud >= 0.5 ? 3 : 4
    return 3
  }
  let sun = hour >= 10 && hour < 15 ? 0 : (hour >= 8 && hour < 10) || (hour >= 15 && hour < 17) ? 1 : 2
  if (cloud >= 0.5) sun = Math.min(2, sun + 1)
  const column = u < 2 ? 0 : u < 3 ? 1 : u < 5 ? 2 : u < 6 ? 3 : 4
  return DAY_CLASS[sun][column]
}

const spread = x => x * x / Math.sqrt(1 + 0.0001 * x)
const sigma = (x, a) => a * x / Math.sqrt(1 + 0.0001 * x)

// The cone's half-width `x` metres downwind, for a tier `length` long: a gaussian plume pinched shut at
// both ends.
export function halfWidth(x, length, a) {
  if (x <= 0 || x >= length) return 0
  return sigma(x, a) * Math.sqrt(2 * Math.log(spread(length) / spread(x)))
}

const N_STEPS = 48

// The line the cone runs along, as `at(along, across)`: the spot `along` metres down it and `across`
// metres to its right. Straight down the forecast wind, or bent along the Thermals API's plume.
export function straightTrack(origin, bearing) {
  const ex = Math.sin(bearing * RAD), ny = Math.cos(bearing * RAD)
  return { at: (along, across) => offset(origin, ex * along + ny * across, ny * along - ex * across) }
}

// The plume's `points` (lon, lat) as a track from the first, carried on straight past the last. Scent pooled
// in a hollow can step back and forth between two cells; the track stops where the path comes back to
// somewhere it has already been, and `looped` says so.
export function plumeTrack(points) {
  const origin = { lat: points[0].lat, lng: points[0].lon }
  const cos = Math.cos(origin.lat * RAD)
  const all = points.map(p => [(p.lon - origin.lng) * M_PER_DEG_LAT * cos, (p.lat - origin.lat) * M_PER_DEG_LAT])
  const steps = all.slice(1).map((q, i) => Math.hypot(q[0] - all[i][0], q[1] - all[i][1])).sort((a, b) => a - b)
  const near = (steps[steps.length >> 1] || 0) * 0.5
  const xy = [all[0]]
  let looped = false
  for (let i = 1; i < all.length && !looped; i++) {
    looped = xy.slice(0, -1).some(q => Math.hypot(all[i][0] - q[0], all[i][1] - q[1]) < near)
    if (!looped) xy.push(all[i])
  }
  if (xy.length < 2) xy.push(all[1])
  const dist = [0]
  for (let i = 1; i < xy.length; i++) dist.push(dist[i - 1] + Math.hypot(xy[i][0] - xy[i - 1][0], xy[i][1] - xy[i - 1][1]))
  const total = dist[dist.length - 1]
  const pos = d => {
    let i = 1
    while (i < xy.length - 1 && dist[i] < d) i++
    const seg = dist[i] - dist[i - 1] || 1, t = (d - dist[i - 1]) / seg
    return [xy[i - 1][0] + (xy[i][0] - xy[i - 1][0]) * t, xy[i - 1][1] + (xy[i][1] - xy[i - 1][1]) * t]
  }
  // The heading over a short stretch either side, so the edges bend smoothly through the path's corners.
  const SMOOTH_M = 20
  return {
    length: total,
    looped,
    at(along, across) {
      const c = pos(along)
      const a = pos(Math.max(0, along - SMOOTH_M)), b = pos(Math.min(Math.max(total, along), along + SMOOTH_M))
      let ex = b[0] - a[0], ny = b[1] - a[1]
      const n = Math.hypot(ex, ny) || 1
      ex /= n; ny /= n
      return offset(origin, c[0] + ny * across, c[1] - ex * across)
    }
  }
}

// A tier as a closed ring of points: down the right edge to the tip and back up the left.
export function tierPolygon(track, length, a) {
  const right = [], left = []
  for (let i = 1; i < N_STEPS; i++) {
    const x = length * Math.pow(i / N_STEPS, 1.3), w = halfWidth(x, length, a)
    right.push(track.at(x, w))
    left.push(track.at(x, -w))
  }
  return [track.at(0, 0), ...right, track.at(length, 0), ...left.reverse()]
}

// Faint streaks riding down the cone, each a line wandering across it.
const STREAK_SIDE = [-0.85, 0.7, -0.1, -0.55, 0.35, 0.9, -0.95, 0.15, -0.3, 0.55, -0.7, 0.8, 0, -0.4]
export function streakLines(track, length, a) {
  const reach = length * 0.95
  return STREAK_SIDE.map((side, k) => {
    const points = []
    for (let i = 0; i <= 40; i++) {
      const x = reach * i / 40, w = halfWidth(x, length, a)
      points.push(track.at(x, w * 0.85 * side + Math.min(w, 1.5) * Math.sin(i * 0.35 + k * 1.7) * Math.min(1, i / 8)))
    }
    return { points, opacity: 0.75 * (1 - 0.8 * side * side) }
  })
}

// With Thermals on, the cone's tiers run the modelled plume's length, at the design's shares of it.
export const plumeTiers = lengthM => [lengthM, lengthM * 0.6, lengthM * 0.3]

// ---- Distance rings ---------------------------------------------------------------------
const RING_STEPS_YD = [50, 100, 250, 500, 880, 1760, 3520, 8800, 17600, 35200, 88000]
const RING_STEPS_M = [50, 100, 250, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000]
const RING_MIN_PX = 38

export const ringLabel = yd => (yd >= 880 ? `${(yd / 1760) % 1 ? (yd / 1760).toFixed(1) : yd / 1760} mi` : `${yd} yd`)
export const ringLabelMetric = m => (m >= 1000 ? `${m / 1000} km` : `${m} m`)

// Rings out to `reachPx` from the cone, spaced so neighbours sit at least RING_MIN_PX apart on screen: yards
// and miles, or metres and kilometres for metric users.
export function rings(metresPerPx, reachPx, metric = false) {
  const steps = metric ? RING_STEPS_M : RING_STEPS_YD
  const unitM = metric ? 1 : M_PER_YD, label = metric ? ringLabelMetric : ringLabel
  const pxPerUnit = unitM / metresPerPx
  const step = steps.find(s => s * pxPerUnit >= RING_MIN_PX) || steps[steps.length - 1]
  const out = []
  for (let n = step; n * pxPerUnit <= reachPx && out.length < 12; n += step) {
    out.push({ metres: n * unitM, label: label(n) })
  }
  return out
}

export function circle(origin, metres, n = 96) {
  const out = []
  for (let i = 0; i < n; i++) out.push(offset(origin, Math.sin(2 * Math.PI * i / n) * metres, Math.cos(2 * Math.PI * i / n) * metres))
  return out
}

// ---- Time scale -------------------------------------------------------------------------
// The wind API's timestamps are UTC, with or without a zone suffix.
export const utcMs = ts => (ts ? moment.utc(ts).valueOf() : NaN)

// The cone's clock: `offset` minutes east of UTC (from the Weather/Get observation), else the browser's.
const wall = (ms, offset) => moment.utc(ms + (offset != null ? offset : -new Date(ms).getTimezoneOffset()) * 60000)

// The hour of day at the cone, with minutes as a fraction, for the plume. The 7-day forecast reads noon.
export function localHour(entry, offset, mode) {
  if (mode === 'daily') return 12
  const ms = entry && entry.timeStamp ? utcMs(entry.timeStamp) : Date.now()
  const t = wall(ms, offset)
  return t.hours() + t.minutes() / 60
}

// The hourly forecast from the hour we are in: the server caches it for an hour, so the first entries can
// already be past.
export function fromNow(hourly, now = Date.now()) {
  const list = hourly || []
  const i = list.findIndex(e => e && utcMs(e.timeStamp) + 3600e3 > now)
  return i < 0 ? [] : list.slice(i)
}

// The hourly slider's stops: up to `past` observed hours (Wind/GetHourly's `observed` entries) before Now,
// then the forecast from the hour we are in. `nowIndex` is Now's place, the number of past stops.
export function hourlyTimeline(hourly, past = 2, now = Date.now()) {
  const list = (hourly || []).filter(Boolean)
  const forecast = fromNow(list.filter(e => !e.observed), now)
  const nowMs = forecast.length ? utcMs(forecast[0].timeStamp) : now
  const observed = list.filter(e => e.observed && utcMs(e.timeStamp) < nowMs && utcMs(e.timeStamp) >= nowMs - past * 3600e3)
  const before = observed.slice(-past)
  return { stops: forecast.length ? [...before, ...forecast] : [], nowIndex: forecast.length ? before.length : 0 }
}

// Now reads the current observation's wind when it is within the hour of the forecast's first entry.
export function withObservation(entry, obs) {
  if (!entry || !obs || typeof obs.windDirDeg !== 'number') return entry
  if (!(Math.abs(utcMs(obs.timeStamp) - utcMs(entry.timeStamp)) <= 3600e3)) return entry
  return { ...entry, direction: obs.windDirDeg, speed: typeof obs.windSpeedMph === 'number' ? obs.windSpeedMph : entry.speed }
}

// Wind/GetDaily sends a day and a night row per date. One stop a date: the day row, or the night row when
// the day's direction is gone (late in the day). Seven at most; `localDate` is the cone's date.
export function dailyStops(rows, offset) {
  const byDate = new Map()
  ;(rows || []).forEach(r => {
    if (!r || !r.timeStamp) return
    const date = wall(utcMs(r.timeStamp), offset).format('YYYY-MM-DD')
    const slot = byDate.get(date) || {}
    slot[r.isDay === false ? 'night' : 'day'] = slot[r.isDay === false ? 'night' : 'day'] || r
    byDate.set(date, slot)
  })
  const out = []
  byDate.forEach(({ day, night }, localDate) => {
    const row = day && day.direction != null ? day : night && night.direction != null ? night : null
    if (row) out.push({ ...row, localDate })
  })
  return out.sort((a, b) => (a.localDate < b.localDate ? -1 : 1)).slice(0, 7)
}

const weekday = (localDate, fmt) => moment.utc(`${localDate}T12:00:00Z`).format(fmt)

// The cone's local date of a stop, YYYY-MM-DD.
export const localDate = (entry, offset) => (entry && entry.timeStamp ? wall(utcMs(entry.timeStamp), offset).format('YYYY-MM-DD') : null)

// The day picker (Matt, 3 Nov, in place of the design's Hourly | 7-Day): today, then each later date the hourly
// forecast reaches, `count` days at most.
export function forecastDays(timeline, offset, count = 8) {
  const { stops = [], nowIndex = 0 } = timeline || {}
  const dates = []
  stops.slice(nowIndex).forEach(e => {
    const d = localDate(e, offset)
    if (d && !dates.includes(d) && dates.length < count) dates.push(d)
  })
  return dates.map((date, i) => ({ date, label: i === 0 ? 'Today' : weekday(date, 'ddd') }))
}

// The slider's stops for a picked day. Today runs from the observed hours to its last hour, with Now where it
// was; a later day is that date's hours, with no Now (nowIndex -1).
export function dayTimeline(timeline, offset, date, isToday) {
  const { stops = [], nowIndex = 0 } = timeline || {}
  if (isToday) {
    const end = stops.findIndex((e, i) => i > nowIndex && localDate(e, offset) !== date)
    return { stops: end < 0 ? stops : stops.slice(0, end), nowIndex }
  }
  return { stops: stops.filter((e, i) => i >= nowIndex && localDate(e, offset) === date), nowIndex: -1 }
}

// What the slider is on: "Now", "Tue 6 PM"; "Today", "Wednesday". A picked later day ('day') reads "Thu 6 AM".
// Contract §7: the forecast (k > 0) is Pro only; Now/Today and the observed past hours are for everyone.
export const forecastLocked = (k, pro) => k > 0 && !pro

export function whenLabel(mode, k, entry, offset) {
  if (mode === 'day') return entry && entry.timeStamp ? wall(utcMs(entry.timeStamp), offset).format('ddd h A') : ''
  if (k === 0) return mode === 'daily' ? 'Today' : 'Now'
  if (!entry || !entry.timeStamp) return ''
  if (mode === 'daily') return entry.localDate ? weekday(entry.localDate, 'dddd') : wall(utcMs(entry.timeStamp), offset).format('dddd')
  const at = wall(utcMs(entry.timeStamp), offset)
  return k < 0 ? `${-k} hr${k < -1 ? 's' : ''} ago · ${at.format('h A')}` : at.format('ddd h A')
}

// A tick per step; `nowIndex` is where Now sits after the past hours. Hourly: majors at the first stop, Now,
// every six hours and the last stop, labelled on Now and the six-hour marks. Daily: every stop, "Today" then
// the weekday.
export function ticks(mode, entries, offset, nowIndex = 0) {
  const list = entries || []
  return list.map((e, k) => {
    if (mode === 'daily') {
      if (k === 0) return { label: 'Today', major: true }
      const label = e && e.localDate ? weekday(e.localDate, 'ddd') : e && e.timeStamp ? wall(utcMs(e.timeStamp), offset).format('ddd') : ''
      return { label, major: true }
    }
    if (k === nowIndex) return { label: 'Now', major: true }
    const t = e && e.timeStamp ? wall(utcMs(e.timeStamp), offset) : null
    const sixth = !!t && t.hours() % 6 === 0
    return { label: sixth ? t.format('h A') : '', major: sixth || k === 0 || k === list.length - 1 }
  })
}

export const coordsLabel = ({ lat, lng }) =>
  `${Math.abs(lat).toFixed(5)}° ${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lng).toFixed(5)}° ${lng >= 0 ? 'E' : 'W'}`

// ---- Thermals ---------------------------------------------------------------------------
// The modelled plume's heading, from the stand to where its scent ends up.
export function plumeBearing(plume) {
  const points = (plume && plume.points) || []
  if (points.length < 2) return null
  const first = points[0], last = points[points.length - 1]
  return bearingTo({ lat: first.lat, lng: first.lon }, { lat: last.lat, lng: last.lon })
}

// Slopes are pulling scent well off the forecast wind's line.
export function thermalsTurning(windFrom, plume) {
  const b = plumeBearing(plume)
  if (b == null || windFrom == null) return false
  const d = Math.abs(normalizeDegrees(b - downwind(windFrom) + 180) - 180)
  return d > 45
}

// The Thermals/Day frame nearest `time` (ms), or null when the day has none within the hour.
export function nearestFrame(day, time) {
  const frames = (day && day.frames) || []
  let best = -1, gap = Infinity
  frames.forEach((f, i) => {
    const d = Math.abs(Date.parse(f.timeUtc) - time)
    if (d < gap) { gap = d; best = i }
  })
  return best >= 0 && gap <= 3600e3 ? best : null
}
